/**
 * Qui sommes-nous V2 - Scroll Animation & Dynamic Rouleau
 * Manages the vertical scroll animation for the tissue roll
 * Adapted from qui-sommes-nous.js
 * 
 * Features:
 * - URL hash navigation: #slide-{id} allows sharing links to specific slides
 * - Browser history integration: back/forward buttons navigate between slides
 * - Works on both desktop (assisted scroll) and mobile (free scroll)
 */

(function() {
  'use strict';

  const SECTION_SELECTOR = '[data-qsn-section]';
  const TEXTURE_ANIM_SELECTOR = '[data-texture-anim]';
  const MOBILE_BREAKPOINT = 768; // px - en dessous = scroll libre, au-dessus = scroll assisté
  // Scroll natif (free) forcé sur tous les écrans, desktop inclus.
  // Le mode "assisté" desktop reste dans le code mais est désactivé ; repasser à false pour le réactiver.
  const FORCE_FREE_SCROLL = true;
  const HASH_PREFIX = 'slide-'; // Préfixe pour les hash d'URL
  const stateMap = new WeakMap();

  if (window.__QuiSommesNousV2Initialized) {
    return;
  }
  window.__QuiSommesNousV2Initialized = true;
  
  // Déterminer si on est sur mobile
  const isMobileDevice = () => window.innerWidth <= MOBILE_BREAKPOINT;
  
  // =========================================
  // URL HASH & HISTORY MANAGEMENT
  // =========================================
  
  /**
   * Get the slide ID from a slide element
   * @param {HTMLElement} slide - The slide element
   * @param {number} index - Fallback index if no custom ID
   * @returns {string} The slide ID
   */
  const getSlideId = (slide, index) => {
    return slide.getAttribute('data-slide-id') || String(index);
  };
  
  /**
   * Find slide index by ID
   * @param {Object} state - Section state
   * @param {string} slideId - The slide ID to find
   * @returns {number} The slide index, or -1 if not found
   */
  const findSlideIndexById = (state, slideId) => {
    // First, try to find by data-slide-id attribute
    const slideIndex = state.slides.findIndex(slide => {
      const id = slide.getAttribute('data-slide-id');
      return id === slideId;
    });
    
    if (slideIndex !== -1) {
      return slideIndex;
    }
    
    // Fallback: try to parse as numeric index
    const numericIndex = parseInt(slideId, 10);
    if (!isNaN(numericIndex) && numericIndex >= 0 && numericIndex < state.slides.length) {
      return numericIndex;
    }
    
    return -1;
  };
  
  /**
   * Get the current hash from URL (without the # and prefix)
   * @returns {string|null} The slide ID from hash, or null
   */
  const getHashSlideId = () => {
    const hash = window.location.hash;
    if (!hash || !hash.startsWith('#' + HASH_PREFIX)) {
      return null;
    }
    return hash.slice(1 + HASH_PREFIX.length); // Remove # and prefix
  };
  
  /**
   * Update the URL hash without triggering a page reload
   * Uses pushState to add to browser history
   * @param {string} slideId - The slide ID to set in hash
   * @param {boolean} replaceState - If true, replace current history entry instead of adding new one
   */
  const updateUrlHash = (slideId, replaceState = false) => {
    const newHash = '#' + HASH_PREFIX + slideId;
    const currentHash = window.location.hash;
    
    // Don't update if hash is already the same
    if (currentHash === newHash) {
      return;
    }
    
    const newUrl = window.location.pathname + window.location.search + newHash;
    
    if (replaceState) {
      history.replaceState({ slideId: slideId, source: 'qsn-v2' }, '', newUrl);
    } else {
      history.pushState({ slideId: slideId, source: 'qsn-v2' }, '', newUrl);
    }
  };
  

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const initAll = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  const calculateTissuePositions = (state) => {
    const visibleHeight = state.tissue.parentElement.offsetHeight;
    
    // Calculer la position de chaque slide en fonction de son offset dans le DOM
    // NOUVEAU: Les slides ont des hauteurs variables, on utilise offsetTop pour chaque slide
    state.tissuePositions = state.slides.map((slide) => {
      const slideTop = slide.offsetTop;
      return -slideTop;
    });
    
    // Calculer également les hauteurs de chaque slide pour les animations
    state.slideHeights = state.slides.map((slide) => slide.offsetHeight);
    
    // Calculer la distance de défilement entre chaque slide consécutive
    state.scrollDistances = [];
    for (let i = 1; i < state.tissuePositions.length; i++) {
      const distance = Math.abs(state.tissuePositions[i] - state.tissuePositions[i - 1]);
      state.scrollDistances.push(distance);
    }
  };

  const updateActiveSlide = (state, index) => {
    state.slides.forEach((slide, i) => {
      const isActive = i === index;
      slide.setAttribute('data-active', isActive ? 'true' : 'false');
    });
    
    // Mettre à jour les dots
    updateDots(state, index);
  };
  
  const updateDots = (state, index) => {
    if (!state.dots || !state.dots.length) {
      return;
    }
    
    state.dots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add('qsn-v2__dot--active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('qsn-v2__dot--active');
        dot.removeAttribute('aria-current');
      }
    });
  };

  const updateBodyScroll = (state) => {
    // Sur mobile avec scroll libre, ne jamais bloquer le scroll
    if (state.scrollMode === 'free') {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      return;
    }
    
    // Sur desktop avec scroll assisté, bloquer sauf sur la dernière slide
    const isOnLastSlide = state.index === state.slides.length - 1;
    
    if (isOnLastSlide) {
      // Débloquer le scroll du body
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    } else {
      // Bloquer le scroll du body
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
  };

  const initializeSlide = (state, slideIndex) => {
    // En mode free scroll, ne pas positionner le tissu via GSAP
    if (state.scrollMode === 'free') {
      updateActiveSlide(state, slideIndex);
      updateBodyScroll(state);
      return;
    }
    
    // En mode assisté, positionner le tissu
    if (state.tissuePositions[slideIndex] !== undefined) {
      state.gsap.set(state.tissue, {
        y: state.tissuePositions[slideIndex]
      });
    }

    updateActiveSlide(state, slideIndex);
    updateBodyScroll(state);
  };
  
  /**
   * MODE FREE SCROLL (Mobile)
   * Détermine quelle slide est actuellement visible
   */
  const getCurrentVisibleSlide = (state) => {
    const container = state.tissue.parentElement;
    const scrollTop = container.scrollTop;
    const containerHeight = container.offsetHeight;
    const viewportCenter = scrollTop + (containerHeight / 2);
    
    // Trouver la slide dont le centre est le plus proche du centre du viewport
    let closestIndex = 0;
    let minDistance = Infinity;
    
    state.slides.forEach((slide, index) => {
      const slideTop = slide.offsetTop;
      const slideHeight = slide.offsetHeight;
      const slideCenter = slideTop + (slideHeight / 2);
      const distance = Math.abs(slideCenter - viewportCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });
    
    return closestIndex;
  };
  
  /**
   * MODE FREE SCROLL (Mobile)
   * Gère le scroll libre et anime le rouleau en conséquence
   */
  const handleFreeScroll = (state) => {
    const container = state.tissue.parentElement;
    const scrollTop = container.scrollTop;
    const maxScroll = container.scrollHeight - container.offsetHeight;
    
    // Calculer le ratio de scroll (0 = haut, 1 = bas)
    const scrollRatio = maxScroll > 0 ? scrollTop / maxScroll : 0;
    
    // Animer la texture du rouleau proportionnellement au scroll
    const textureAnim = state.section.querySelector(TEXTURE_ANIM_SELECTOR);
    if (textureAnim) {
      // Distance totale que la texture doit parcourir
      const totalScrollableHeight = container.scrollHeight - container.offsetHeight;
      
      // Position de la texture proportionnelle au scroll
      // On inverse le signe car quand on scrolle vers le bas, la texture doit monter
      const textureY = scrollRatio * totalScrollableHeight;
      
      state.gsap.set(textureAnim, {
        backgroundPositionY: `${textureY}px`
      });
    }
    
    // Mettre à jour la slide active
    const visibleSlide = getCurrentVisibleSlide(state);
    if (visibleSlide !== state.index) {
      const previousIndex = state.index;
      state.index = visibleSlide;
      updateDots(state, state.index);
      
      // Update URL hash when slide changes during free scroll
      // Use replaceState to avoid cluttering history with every scroll position
      const slideId = getSlideId(state.slides[state.index], state.index);
      updateUrlHash(slideId, true); // true = replaceState
    }
  };

  const processQueuedTransitions = (state) => {
    if (state.animating) {
      return;
    }

    const direction = state.queuedDirections.shift();
    if (!direction) {
      return;
    }

    transitionSlide(state, direction);
  };

  const queueDirection = (state, direction) => {
    if (!direction || (direction !== 'up' && direction !== 'down')) {
      return;
    }

    const delta = direction === 'down' ? 1 : -1;
    const targetIndex = state.pendingIndex + delta;

    if (targetIndex < 0 || targetIndex >= state.slides.length) {
      return;
    }

    state.queuedDirections.push(direction);
    state.pendingIndex = targetIndex;
    processQueuedTransitions(state);
  };

  const transitionSlide = (state, direction) => {
    if (state.animating) {
      return;
    }

    const nextIndex = state.index + (direction === 'down' ? 1 : -1);
    if (nextIndex < 0 || nextIndex >= state.slides.length) {
      return;
    }

    // Vérifier que la page est en haut avant de changer de slide
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    if (scrollTop > 0) {
      // Scroller en haut d'abord, puis déclencher l'animation
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Attendre que le scroll soit terminé avant de déclencher l'animation
      const checkScroll = () => {
        const currentScrollTop = window.scrollY || document.documentElement.scrollTop;
        if (currentScrollTop === 0) {
          // Maintenant on peut animer
          performSlideTransition(state, direction, nextIndex);
        } else {
          // Réessayer dans 50ms
          setTimeout(checkScroll, 50);
        }
      };
      setTimeout(checkScroll, 100);
      return;
    }

    performSlideTransition(state, direction, nextIndex);
  };
  
  const performSlideTransition = (state, direction, nextIndex) => {
    state.animating = true;
    state.activeDirection = direction;

    const nextTissueY = state.tissuePositions[nextIndex];
    const animationDuration = 1.5;
    
    // Calculer la distance réelle parcourue pour adapter l'animation du rouleau
    const currentY = state.tissuePositions[state.index];
    const scrollDistance = Math.abs(nextTissueY - currentY);

    // Animer le rouleau en synchronisation avec les slides
    // NOUVEAU: Passer la distance réelle pour une animation proportionnelle
    animateRouleauTexture(state, direction, animationDuration, scrollDistance);

    // Animer uniquement le tissu (les slides suivent automatiquement)
    state.gsap.to(state.tissue, {
      y: nextTissueY,
      duration: animationDuration,
      ease: 'power2.inOut',
      onComplete: () => {
        state.index = nextIndex;
        state.animating = false;
        state.activeDirection = null;
        updateActiveSlide(state, state.index);
        updateBodyScroll(state);
        processQueuedTransitions(state);
        
        if (!state.animating && state.queuedDirections.length === 0) {
          state.pendingIndex = state.index;
        }
        
        // Update URL hash after transition completes (unless navigating from popstate)
        if (!state.skipHashUpdate) {
          const slideId = getSlideId(state.slides[state.index], state.index);
          updateUrlHash(slideId);
        }
        state.skipHashUpdate = false;
      }
    });
  };
  
  const transitionToSlide = (state, targetIndex) => {
    if (state.animating || targetIndex === state.index) {
      return;
    }
    
    if (targetIndex < 0 || targetIndex >= state.slides.length) {
      return;
    }
    
    // Vérifier que la page est en haut avant de changer de slide
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    if (scrollTop > 0) {
      // Scroller en haut d'abord, puis déclencher l'animation
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Attendre que le scroll soit terminé avant de déclencher l'animation
      const checkScroll = () => {
        const currentScrollTop = window.scrollY || document.documentElement.scrollTop;
        if (currentScrollTop === 0) {
          // Maintenant on peut animer
          performDirectSlideTransition(state, targetIndex);
        } else {
          // Réessayer dans 50ms
          setTimeout(checkScroll, 50);
        }
      };
      setTimeout(checkScroll, 100);
      return;
    }
    
    performDirectSlideTransition(state, targetIndex);
  };
  
  const performDirectSlideTransition = (state, targetIndex) => {
    state.animating = true;
    
    const distance = Math.abs(targetIndex - state.index);
    const direction = targetIndex > state.index ? 'down' : 'up';
    const nextTissueY = state.tissuePositions[targetIndex];
    const currentY = state.tissuePositions[state.index];
    
    // NOUVEAU: Calculer la distance réelle parcourue en pixels
    const realScrollDistance = Math.abs(nextTissueY - currentY);
    
    // Durée de l'animation augmente avec la distance (mais avec un max)
    const baseDuration = 1.5;
    const animationDuration = Math.min(baseDuration + (distance - 1) * 0.3, 2.5);
    
    // Animer le rouleau avec la distance réelle
    animateRouleauTextureMultiple(state, direction, distance, animationDuration, realScrollDistance);
    
    // Animer le tissu
    state.gsap.to(state.tissue, {
      y: nextTissueY,
      duration: animationDuration,
      ease: 'power2.inOut',
      onComplete: () => {
        state.index = targetIndex;
        state.animating = false;
        state.activeDirection = null;
        state.pendingIndex = targetIndex;
        updateActiveSlide(state, state.index);
        updateBodyScroll(state);
        
        // Update URL hash after transition completes (unless triggered by popstate)
        if (!state.skipHashUpdate) {
          const slideId = getSlideId(state.slides[state.index], state.index);
          updateUrlHash(slideId);
        }
        state.skipHashUpdate = false;
      }
    });
  };

  /**
   * Animate rouleau texture synchronized with slide transitions
   * @param {Object} state - Section state
   * @param {string} direction - 'up' or 'down'
   * @param {number} duration - Animation duration in seconds
   * @param {number} realScrollDistance - Real distance scrolled in pixels (optional)
   */
  const animateRouleauTexture = (state, direction, duration = 1.5, realScrollDistance = null) => {
    const textureAnim = state.section.querySelector(TEXTURE_ANIM_SELECTOR);
    if (!textureAnim) {
      return;
    }
    
    // NOUVEAU SYSTÈME: Animation proportionnelle à la distance réelle
    // Si realScrollDistance est fourni, on l'utilise pour une animation plus naturelle
    let scrollDistance;
    
    if (realScrollDistance !== null && realScrollDistance > 0) {
      // Utiliser la distance réelle pour une animation proportionnelle
      // Le rouleau tourne proportionnellement à la distance parcourue
      scrollDistance = direction === 'down' ? realScrollDistance : -realScrollDistance;
    } else {
      // Fallback: utiliser la hauteur de texture par défaut (680px)
      const textureHeight = 680;
      scrollDistance = direction === 'down' ? textureHeight : -textureHeight;
    }
    
    // Get current background position
    const currentBgPosition = getComputedStyle(textureAnim).backgroundPositionY;
    const currentY = parseInt(currentBgPosition) || 0;
    
    // Target position
    const targetY = currentY + scrollDistance;
    
    // Animate with GSAP - synchronized with slide animation
    state.gsap.to(textureAnim, {
      backgroundPositionY: `${targetY}px`,
      duration: duration,
      ease: 'power2.inOut'
      // Pas de onComplete : la position reste à targetY
      // Le background-repeat gère le seamless loop automatiquement
    });
  };
  
  /**
   * Animate rouleau texture for multiple slide jumps
   * @param {Object} state - Section state
   * @param {string} direction - 'up' or 'down'
   * @param {number} distance - Number of slides to skip
   * @param {number} duration - Animation duration in seconds
   * @param {number} realScrollDistance - Real total distance scrolled in pixels (optional)
   */
  const animateRouleauTextureMultiple = (state, direction, distance, duration = 1.5, realScrollDistance = null) => {
    const textureAnim = state.section.querySelector(TEXTURE_ANIM_SELECTOR);
    if (!textureAnim) {
      return;
    }
    
    // NOUVEAU SYSTÈME: Animation proportionnelle à la distance réelle
    let scrollDistance;
    
    if (realScrollDistance !== null && realScrollDistance > 0) {
      // Utiliser la distance réelle totale pour une animation proportionnelle
      scrollDistance = direction === 'down' ? realScrollDistance : -realScrollDistance;
    } else {
      // Fallback: utiliser la hauteur de texture par défaut × nombre de slides
      const textureHeight = 680;
      scrollDistance = (direction === 'down' ? textureHeight : -textureHeight) * distance;
    }
    
    // Get current background position
    const currentBgPosition = getComputedStyle(textureAnim).backgroundPositionY;
    const currentY = parseInt(currentBgPosition) || 0;
    
    // Target position
    const targetY = currentY + scrollDistance;
    
    // Animate with GSAP
    state.gsap.to(textureAnim, {
      backgroundPositionY: `${targetY}px`,
      duration: duration,
      ease: 'power2.inOut'
    });
  };

  const initSection = (section) => {
    if (stateMap.has(section)) {
      return;
    }

    const gsap = window.gsap;
    if (!gsap) {
      console.error('[QSN V2] GSAP is required');
      return;
    }

    const tissue = section.querySelector('[data-tissue-scroll]');
    const slides = Array.from(section.querySelectorAll('[data-slide]'));
    const dots = Array.from(section.querySelectorAll('[data-dot]'));
    
    if (!tissue || !slides.length) {
      return;
    }

    const state = {
      section,
      gsap,
      tissue,
      slides,
      dots,
      index: 0,
      animating: false,
      tissuePositions: [],
      slideHeights: [],        // NOUVEAU: Hauteurs réelles de chaque slide
      scrollDistances: [],     // NOUVEAU: Distances entre slides consécutives
      scrollMode: (FORCE_FREE_SCROLL || isMobileDevice()) ? 'free' : 'assisted', // free forcé sur tous les écrans
      resizeTimer: null,
      startY: null,
      lastWheelTime: 0,
      queuedDirections: [],
      pendingIndex: 0,
      activeDirection: null,
      scrollTimer: null,       // Timer pour throttle du scroll en mode free
      skipHashUpdate: false    // Flag to prevent hash update during popstate navigation
    };

    stateMap.set(section, state);

    const handleLoad = () => {
      calculateTissuePositions(state);
      
      // Check if there's a hash in the URL for initial navigation
      const hashSlideId = getHashSlideId();
      let initialSlideIndex = 0;
      
      if (hashSlideId) {
        const targetIndex = findSlideIndexById(state, hashSlideId);
        if (targetIndex !== -1) {
          initialSlideIndex = targetIndex;
        }
      }
      
      // Initialize at the target slide (either from hash or first slide)
      initializeSlide(state, initialSlideIndex);
      state.index = initialSlideIndex;
      state.pendingIndex = initialSlideIndex;
      
      // Set initial URL hash (use replaceState to not add to history)
      const slideId = getSlideId(state.slides[initialSlideIndex], initialSlideIndex);
      updateUrlHash(slideId, true);
      
      // In free mode, scroll to the target slide and update the scroll handler
      if (state.scrollMode === 'free') {
        if (initialSlideIndex > 0) {
          // Scroll to the target slide instantly
          const container = state.tissue.parentElement;
          const targetSlide = state.slides[initialSlideIndex];
          if (container && targetSlide) {
            container.scrollTop = targetSlide.offsetTop;
          }
        }
        handleFreeScroll(state);
      }
    };
    
    /**
     * Handle browser back/forward navigation (popstate)
     */
    const handlePopState = (event) => {
      // Check if this is a navigation within our slide system
      const hashSlideId = getHashSlideId();
      
      if (!hashSlideId) {
        // No hash or not our hash, might be navigating away from the section
        return;
      }
      
      const targetIndex = findSlideIndexById(state, hashSlideId);
      
      if (targetIndex === -1 || targetIndex === state.index) {
        // Slide not found or already on target slide
        return;
      }
      
      // Set flag to prevent updating hash (we're navigating FROM the hash)
      state.skipHashUpdate = true;
      
      // Navigate to the target slide with animation
      if (state.scrollMode === 'free') {
        // En mode free scroll, scroller vers la slide cible
        const targetSlide = state.slides[targetIndex];
        if (targetSlide) {
          const container = state.tissue.parentElement;
          const targetOffset = targetSlide.offsetTop;
          
          // Smooth scroll vers la slide
          container.scrollTo({
            top: targetOffset,
            behavior: 'smooth'
          });
          
          // Mettre à jour l'index
          state.index = targetIndex;
          updateDots(state, state.index);
          state.skipHashUpdate = false;
        }
      } else {
        // En mode assisté, utiliser l'animation GSAP
        // Annuler les transitions en queue
        state.queuedDirections = [];
        state.pendingIndex = state.index;
        
        // Navigate without updating history (reuse transitionToSlide)
        transitionToSlide(state, targetIndex);
      }
    };

    const handleResize = () => {
      window.clearTimeout(state.resizeTimer);
      state.resizeTimer = window.setTimeout(() => {
        // Déterminer le nouveau mode après resize
        const newMode = (FORCE_FREE_SCROLL || isMobileDevice()) ? 'free' : 'assisted';
        const modeChanged = newMode !== state.scrollMode;
        
        if (modeChanged) {
          // Le mode a changé, réinitialiser
          state.scrollMode = newMode;
          updateBodyScroll(state);
          
          // Retirer les anciens event listeners et en ajouter de nouveaux
          cleanupEventListeners(state);
          attachEventListeners(state);
        }
        
        calculateTissuePositions(state);
        
        // En mode assisté, repositionner le tissu
        if (state.scrollMode === 'assisted' && state.tissuePositions[state.index] !== undefined) {
          state.gsap.set(state.tissue, {
            y: state.tissuePositions[state.index]
          });
        }
      }, 100);
    };

    const handleWheel = (event) => {
      // En mode free scroll, laisser le scroll natif
      if (state.scrollMode === 'free') {
        return;
      }
      
      const now = Date.now();
      if (now - state.lastWheelTime <= 80) {
        return;
      }

      if (state.animating || state.queuedDirections.length > 0) {
        return;
      }

      if (Math.abs(event.deltaY) < 8) {
        return;
      }

      const direction = event.deltaY > 0 ? 'down' : 'up';
      const isOnLastSlide = state.index === state.slides.length - 1;
      const isScrollingDown = direction === 'down';
      const isScrollingUp = direction === 'up';
      
      // Sur la dernière slide, gérer le scroll natif du footer
      if (isOnLastSlide) {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        
        if (isScrollingDown) {
          // Laisser le scroll natif vers le bas pour afficher le footer
          return;
        }
        
        if (isScrollingUp && scrollTop > 0) {
          // Le footer est encore visible, laisser le scroll natif remonter
          return;
        }
        
        // scrollTop === 0 et on scrolle vers le haut : activer l'animation de slide
      }

      // Bloquer le scroll et gérer la navigation entre slides
      event.preventDefault();
      state.lastWheelTime = now;
      queueDirection(state, direction);
    };

    const handleTouchStart = (event) => {
      // En mode free scroll, laisser le scroll natif
      if (state.scrollMode === 'free') {
        return;
      }
      
      state.startY = event.touches[0].clientY;
    };

    const handleTouchEnd = (event) => {
      // En mode free scroll, laisser le scroll natif
      if (state.scrollMode === 'free') {
        return;
      }
      
      if (state.startY == null) {
        return;
      }
      const deltaY = state.startY - event.changedTouches[0].clientY;
      const direction = deltaY > 0 ? 'down' : 'up';
      
      // Sur la dernière slide, gérer le scroll natif du footer
      const isOnLastSlide = state.index === state.slides.length - 1;
      if (isOnLastSlide) {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        
        if (direction === 'down') {
          // Laisser le scroll natif vers le bas pour afficher le footer
          state.startY = null;
          return;
        }
        
        if (direction === 'up' && scrollTop > 0) {
          // Le footer est encore visible, laisser le scroll natif remonter
          state.startY = null;
          return;
        }
        
        // scrollTop === 0 et on swipe vers le haut : activer l'animation de slide
      }
      
      if (Math.abs(deltaY) > 25 && !state.animating && state.queuedDirections.length === 0) {
        queueDirection(state, direction);
      }
      state.startY = null;
    };

    const handleKeyDown = (event) => {
      // En mode free scroll, laisser le comportement natif des touches
      if (state.scrollMode === 'free') {
        return;
      }
      
      const isOnLastSlide = state.index === state.slides.length - 1;
      
      if (['ArrowDown', 'PageDown', ' '].includes(event.key)) {
        // Sur la dernière slide, laisser le scroll natif vers le bas
        if (isOnLastSlide) {
          return;
        }
        event.preventDefault();
        queueDirection(state, 'down');
      } else if (['ArrowUp', 'PageUp'].includes(event.key)) {
        // Sur la dernière slide, ne remonter que si le footer n'est plus visible
        if (isOnLastSlide) {
          const scrollTop = window.scrollY || document.documentElement.scrollTop;
          if (scrollTop > 0) {
            // Le footer est encore visible, laisser le scroll natif
            return;
          }
          // scrollTop === 0 : activer l'animation de slide
        }
        event.preventDefault();
        queueDirection(state, 'up');
      }
    };
    
    const handleContainerScroll = (event) => {
      // Ce handler n'est actif qu'en mode free scroll
      if (state.scrollMode !== 'free') {
        return;
      }
      
      // Throttle le scroll pour de meilleures performances
      if (state.scrollTimer) {
        return;
      }
      
      state.scrollTimer = setTimeout(() => {
        handleFreeScroll(state);
        state.scrollTimer = null;
      }, 16); // ~60fps
    };
    
    const handleDotClick = (event) => {
      const dot = event.currentTarget;
      const targetIndex = parseInt(dot.getAttribute('data-slide-target'), 10);
      
      if (isNaN(targetIndex) || targetIndex === state.index) {
        return;
      }
      
      if (state.scrollMode === 'free') {
        // En mode free scroll, scroller vers la slide cible
        const targetSlide = state.slides[targetIndex];
        if (targetSlide) {
          const container = state.tissue.parentElement;
          const targetOffset = targetSlide.offsetTop;
          
          // Smooth scroll vers la slide
          container.scrollTo({
            top: targetOffset,
            behavior: 'smooth'
          });
          
          // Mettre à jour immédiatement l'index (sera confirmé par handleFreeScroll)
          state.index = targetIndex;
          updateDots(state, state.index);
          
          // Update URL hash when clicking a dot
          const slideId = getSlideId(state.slides[targetIndex], targetIndex);
          updateUrlHash(slideId);
        }
      } else {
        // En mode assisté, utiliser l'animation GSAP
        // Annuler les transitions en queue
        state.queuedDirections = [];
        state.pendingIndex = state.index;
        
        // Aller directement à la slide cible
        transitionToSlide(state, targetIndex);
      }
    };

    // FREE MODE — "gate" du footer : tant que le rouleau (conteneur interne) n'est
    // pas arrivé tout en bas, la molette pilote le conteneur en scroll natif (inertie
    // préservée). Uniquement aux extrémités on relaie vers la fenêtre pour révéler le
    // footer (vers le bas) ou le masquer (vers le haut) — sinon Chrome "latche" le
    // scroll au conteneur et ne propage pas toujours seul jusqu'à la fenêtre.
    const handleFreeBoundaryWheel = (event) => {
      const container = state.tissue.parentElement;
      if (!container) {
        return;
      }
      let deltaY = event.deltaY;
      if (event.deltaMode === 1) {
        deltaY *= 16; // lignes -> pixels
      } else if (event.deltaMode === 2) {
        deltaY *= container.clientHeight; // pages -> pixels
      }
      const winScroll = window.scrollY || document.documentElement.scrollTop || 0;
      if (deltaY > 0) {
        // Vers le bas : ne révéler le footer qu'une fois le rouleau déroulé jusqu'en bas
        const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
        if (atBottom) {
          event.preventDefault();
          // 'instant' : la fenêtre a scroll-behavior:smooth, mais une molette doit
          // répondre au tac (comme un scroll natif), sans animation à la traîne.
          window.scrollBy({ top: deltaY, left: 0, behavior: 'instant' });
        }
        // sinon : laisser le scroll natif piloter le conteneur
      } else if (winScroll > 0) {
        // Vers le haut : si le footer est visible, le masquer avant de re-piloter le rouleau
        event.preventDefault();
        window.scrollBy({ top: deltaY, left: 0, behavior: 'instant' });
      }
      // sinon (vers le haut, fenêtre déjà en haut) : scroll natif du conteneur
    };

    state.handlers = {
      handleLoad,
      handleResize,
      handleWheel,
      handleTouchStart,
      handleTouchEnd,
      handleKeyDown,
      handleDotClick,
      handleContainerScroll,
      handleFreeBoundaryWheel,
      handlePopState
    };
    
    // Fonction pour nettoyer les event listeners
    const cleanupEventListeners = (state) => {
      const container = state.tissue.parentElement;
      
      section.removeEventListener('wheel', state.handlers.handleWheel);
      section.removeEventListener('wheel', state.handlers.handleFreeBoundaryWheel);
      section.removeEventListener('touchstart', state.handlers.handleTouchStart);
      section.removeEventListener('touchend', state.handlers.handleTouchEnd);
      document.removeEventListener('keydown', state.handlers.handleKeyDown);
      window.removeEventListener('popstate', state.handlers.handlePopState);
      
      if (container) {
        container.removeEventListener('scroll', state.handlers.handleContainerScroll);
      }
    };
    
    // Fonction pour attacher les event listeners selon le mode
    const attachEventListeners = (state) => {
      const container = state.tissue.parentElement;
      
      // Always listen for popstate (browser back/forward buttons)
      window.addEventListener('popstate', state.handlers.handlePopState);
      
      if (state.scrollMode === 'assisted') {
        // Mode assisté : bloquer le scroll natif et gérer avec GSAP
        section.addEventListener('wheel', state.handlers.handleWheel, { passive: false });
        section.addEventListener('touchstart', state.handlers.handleTouchStart, { passive: true });
        section.addEventListener('touchend', state.handlers.handleTouchEnd, { passive: true });
        document.addEventListener('keydown', state.handlers.handleKeyDown);
      } else {
        // Mode free : scroll natif du conteneur + relais "gate" du footer aux extrémités
        section.addEventListener('wheel', state.handlers.handleFreeBoundaryWheel, { passive: false });
        if (container) {
          container.addEventListener('scroll', state.handlers.handleContainerScroll, { passive: true });
        }
      }
    };
    
    // Stocker les fonctions de nettoyage dans le state
    state.cleanupEventListeners = cleanupEventListeners;
    state.attachEventListeners = attachEventListeners;

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad, { once: true });
    }

    window.addEventListener('resize', handleResize);
    
    // Attacher les event listeners selon le mode initial
    attachEventListeners(state);
    
    // Ajouter les event listeners sur les dots (toujours actifs)
    state.dots.forEach((dot) => {
      dot.addEventListener('click', handleDotClick);
    });

  };

  const destroySection = (section) => {
    const state = stateMap.get(section);
    if (!state) {
      return;
    }

    window.removeEventListener('resize', state.handlers.handleResize);
    
    // Utiliser la fonction de nettoyage pour retirer les event listeners selon le mode
    if (state.cleanupEventListeners) {
      state.cleanupEventListeners(state);
    }
    
    // Retirer les event listeners des dots
    if (state.dots && state.dots.length) {
      state.dots.forEach((dot) => {
        dot.removeEventListener('click', state.handlers.handleDotClick);
      });
    }

    if (document.readyState !== 'complete') {
      window.removeEventListener('load', state.handlers.handleLoad);
    }

    // Restaurer le scroll du body
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';

    state.queuedDirections = [];
    state.pendingIndex = 0;
    state.activeDirection = null;

    window.clearTimeout(state.resizeTimer);
    window.clearTimeout(state.scrollTimer);
    
    stateMap.delete(section);
  };

  // Wait for GSAP
  function waitForGSAP() {
    if (typeof gsap !== 'undefined') {
      ready(() => {
        initAll();

        document.addEventListener('shopify:section:load', (event) => {
          const section = event.target;
          if (section && section.matches(SECTION_SELECTOR)) {
            initSection(section);
          }
        });

        document.addEventListener('shopify:section:unload', (event) => {
          const section = event.target;
          if (section && section.matches(SECTION_SELECTOR)) {
            destroySection(section);
          }
        });
      });
    } else {
      setTimeout(waitForGSAP, 100);
    }
  }

  waitForGSAP();
})();
